import { useCallback, useEffect, useState } from "react";
import { Wrench, FilePdf } from "@phosphor-icons/react";
import SubmissionsTable from "@/components/SubmissionsTable";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";

export default function WorkshopPanel({ user }) {
  const [subs, setSubs] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const refresh = useCallback(() => {
    api.get("/submissions").then((r) => setSubs(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="space-y-10" data-testid="workshop-panel">
      <div className="rounded-sm border border-primary/40 bg-primary/5 px-5 py-4">
        <p className="flex items-center gap-2 text-sm">
          <Wrench size={18} className="text-primary" />
          Viewing vehicle history for workshop: <span className="font-bold text-primary" data-testid="workshop-company-name">{user.workshop_company || "—"}</span>
        </p>
      </div>

      <section className="space-y-4">
        <h3 className="font-display text-xl font-bold tracking-tight">Vehicles Sent to Your Workshop</h3>
        <p className="text-sm text-muted-foreground">Live status and progress of every PSS Towing job referred to you. Open a job to view details and photos.</p>
        <SubmissionsTable submissions={subs} onRefresh={refresh} />
      </section>

      <section className="space-y-4">
        <h3 className="font-display text-xl font-bold tracking-tight">Associated Invoices</h3>
        <div className="overflow-x-auto rounded-sm border border-border" data-testid="workshop-invoices-table">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Invoice No</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Job</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Customer</TableHead>
                <TableHead className="text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">Total</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Date</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow key={inv.id} className="border-border hover:bg-muted/50" data-testid={`workshop-invoice-${inv.id}`}>
                  <TableCell className="font-mono text-sm font-bold text-primary">{inv.invoice_no}</TableCell>
                  <TableCell className="font-mono text-xs">{inv.job_no || "—"}</TableCell>
                  <TableCell className="text-sm">{inv.customer_name || "—"}</TableCell>
                  <TableCell className="text-right font-mono font-bold">RM {(inv.total ?? 0).toFixed(2)}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{inv.date}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" data-testid={`workshop-invoice-dl-${inv.id}`}
                      onClick={() => downloadFile(`/invoices/${inv.id}/file`, `${inv.invoice_no}.pdf`).catch((e) => toast.error(formatApiError(e)))}
                      className="rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-amber-400">
                      <FilePdf size={14} className="mr-1" /> PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!invoices.length && (
                <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">No invoices yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}
